import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MetaTagConfig } from './metaTags';
import { StructuredData, injectStructuredData } from './structuredData';

interface SEOHeadProps {
    title: string;
    description?: string;
    keywords?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    canonicalUrl?: string;
    language?: string;
    structuredData?: StructuredData | StructuredData[];
    noindex?: boolean;
}

/**
 * SEOHead Component
 * Dynamically updates document meta tags for SEO optimization
 * Supports both English and Hindi content
 * Injects structured data (Schema.org JSON-LD) for rich snippets
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
    title,
    description = 'BhriguGPT - AI-Powered Vedic Astrology Platform',
    keywords = '',
    ogTitle,
    ogDescription,
    ogImage = '/bhrigoo_rishi.png',
    canonicalUrl,
    language = 'en',
    structuredData,
    noindex = false
}) => {
    const location = useLocation();
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bhrigugpt.com';

    useEffect(() => {
        // Update page title (append site name if not already included)
        document.title = title.includes('BhriguGPT') ? title : `${title} | BhriguGPT`;

        // Update or create meta tags
        const updateMetaTag = (property: string, content: string, isProperty: boolean = false) => {
            const attributeName = isProperty ? 'property' : 'name';
            let element = document.querySelector(`meta[${attributeName}="${property}"]`);

            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attributeName, property);
                document.head.appendChild(element);
            }

            element.setAttribute('content', content);
        };

        // Standard meta tags
        updateMetaTag('description', description);
        updateMetaTag('keywords', keywords);

        // Language and locale
        updateMetaTag('language', language === 'hi' ? 'Hindi' : 'English');
        const htmlElement = document.documentElement;
        htmlElement.setAttribute('lang', language === 'hi' ? 'hi' : 'en');

        // Open Graph meta tags
        updateMetaTag('og:title', ogTitle || title, true);
        updateMetaTag('og:description', ogDescription || description, true);
        updateMetaTag('og:type', 'website', true);
        updateMetaTag('og:url', `${siteUrl}${canonicalUrl || location.pathname}`, true);

        updateMetaTag('og:image', `${siteUrl}${ogImage}`, true);
        updateMetaTag('og:image:alt', title, true);

        updateMetaTag('og:site_name', 'BhriguGPT', true);
        updateMetaTag('og:locale', language === 'hi' ? 'hi_IN' : 'en_US', true);

        // Twitter Card meta tags
        updateMetaTag('twitter:card', 'summary_large_image');
        updateMetaTag('twitter:title', ogTitle || title);
        updateMetaTag('twitter:description', ogDescription || description);

        updateMetaTag('twitter:image', `${siteUrl}${ogImage}`);

        // Canonical URL
        let canonicalLink = document.querySelector('link[rel="canonical"]');
        if (!canonicalLink) {
            canonicalLink = document.createElement('link');
            canonicalLink.setAttribute('rel', 'canonical');
            document.head.appendChild(canonicalLink);
        }
        canonicalLink.setAttribute('href', `${siteUrl}${canonicalUrl || location.pathname}`);

        // Hreflang alternate links
        document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
        const currentPath = canonicalUrl || location.pathname;
        const enPath = currentPath.replace(/\/hindi(\/|$)/, '/english$1');
        const hiPath = currentPath.replace(/\/english(\/|$)/, '/hindi$1');
        const addHreflang = (lang: string, href: string) => {
            const link = document.createElement('link');
            link.setAttribute('rel', 'alternate');
            link.setAttribute('hreflang', lang);
            link.setAttribute('href', `${siteUrl}${href}`);
            document.head.appendChild(link);
        };
        addHreflang('en', enPath);
        addHreflang('hi', hiPath);
        addHreflang('x-default', enPath);

        // Additional SEO meta tags
        const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';
        updateMetaTag('robots', robotsContent);
        updateMetaTag('googlebot', robotsContent);
        updateMetaTag('author', 'BhriguGPT');
        updateMetaTag('revisit-after', '7 days');

        // Mobile optimization - Careful not to overwrite critical index.html settings for iOS
        let viewportMeta = document.querySelector('meta[name="viewport"]');
        if (!viewportMeta) {
            viewportMeta = document.createElement('meta');
            viewportMeta.setAttribute('name', 'viewport');
            viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0, viewport-fit=cover');
            document.head.appendChild(viewportMeta);
        } else {
            // If it already exists (from index.html), ensure it has viewport-fit=cover but don't strip everything else
            const currentContent = viewportMeta.getAttribute('content') || '';
            if (!currentContent.includes('viewport-fit=cover')) {
                viewportMeta.setAttribute('content', `${currentContent}, viewport-fit=cover`);
            }
        }

        // Theme color
        let themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (!themeColorMeta) {
            themeColorMeta = document.createElement('meta');
            themeColorMeta.setAttribute('name', 'theme-color');
            document.head.appendChild(themeColorMeta);
        }
        themeColorMeta.setAttribute('content', '#1a1a2e');

        // Clear stale JSON-LD when navigating between SPA routes.
        document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

        // Inject structured data if provided
        if (structuredData) {
            injectStructuredData(structuredData);
        }

    }, [title, description, keywords, ogTitle, ogDescription, ogImage, canonicalUrl, language, location.pathname, siteUrl, structuredData, noindex]);

    // This component doesn't render anything visible
    return null;
};

/**
 * Helper hook to use SEO meta tags
 */
export const useSEO = (metaConfig: MetaTagConfig, language: string = 'English') => {
    return {
        ...metaConfig,
        language: language.toLowerCase() === 'hindi' ? 'hi' : 'en'
    };
};
